import React from "react";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import { NumberInput, Textarea } from "@mantine/core";
import RemoteSelect2 from "../../components/remoteSelect2";
import { useCellRender } from "../../hooks/useCellRender";
import {
  consultantLabelFromPointTransaction,
  isSystemPostedBy,
  POINT_ORDER_COLUMN_TIP,
  POINT_POSTED_BY_COLUMN_TIP,
  POINT_TXN_COLUMN_TIP,
  POINT_TYPE_COLUMN_TIP,
} from "../../helpers/pointTransactionDisplay";
import moment from "moment";

const apiEntity = "pointTransaction";

const PointTransaction = () => {
  const [cellRender] = useCellRender();

  const tableSchema = {
    showActionColumn: false,
    disableRowDoubleClick: true,
    columns: [
      { field: "code", headerName: "Code" },
      {
        field: "postedAt",
        headerName: "Posted",
        cellRender: (col, row) =>
          row.postedAt ? moment(row.postedAt).format("YYYY-MM-DD HH:mm") : "-",
      },
      {
        field: "postedBy",
        headerName: "Posted By",
        headerTooltip: POINT_POSTED_BY_COLUMN_TIP,
        cellRender: (col, row) =>
          isSystemPostedBy(row.postedBy) ? "SYSTEM" : cellRender.User(row.postedBy),
      },
      {
        field: "pointType",
        headerName: "Point Type",
        headerTooltip: POINT_TYPE_COLUMN_TIP,
        cellRender: (col, row) =>
          row.pointType?.name || row.pointType?.code || "-",
      },
      {
        field: "type",
        headerName: "Type",
        headerTooltip: POINT_TXN_COLUMN_TIP,
      },
      {
        field: "order",
        headerName: "Order",
        headerTooltip: POINT_ORDER_COLUMN_TIP,
        cellRender: (col, row) => row.order?.code || "-",
      },
      {
        field: "user",
        headerName: "Consultant",
        cellRender: (col, row) => consultantLabelFromPointTransaction(row),
      },
      {
        field: "amount",
        headerName: "Points",
        cellRender: (col, row) => cellRender.Currency(row.amount, "USD"),
      },
      { field: "remark", headerName: "Remark" },
    ],
    searchableFields: ["code", "type", "remark"],
  };

  const formSchema = {
    title: "Point Adjustment",
    display: {
      mode: ENUM_FORM_DISPLAY.DRAWER,
      size: "lg",
      position: "right",
    },
    apiEntity,
    initialValues: {
      type: "ADJUSTMENT",
      amount: 0,
      remark: "",
    },
    layout: {
      containers: [
        {
          key: "main",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Adjustment",
            description:
              "Posted rows cannot be edited. To correct a wrong amount, add another ADJUSTMENT here. Use a negative amount to reverse.",
          },
        },
      ],
      fields: [
        {
          name: "pointType",
          parent: "main",
          component: RemoteSelect2,
          props: {
            required: true,
            label: "Point Type",
            apiEntity: "pointType",
            valueField: "_id",
            labelField: "name",
            labelRender: (data) => `${data.code || ""} ${data.name || ""}`,
            searchFields: ["code", "name", "cname"],
            clearable: true,
          },
        },
        {
          name: "order",
          parent: "main",
          component: RemoteSelect2,
          props: {
            label: "Order",
            apiEntity: "order",
            valueField: "_id",
            labelField: "code",
            labelRender: (data) =>
              `${data.code || ""} ${data.policyNumber || ""}`,
            searchFields: ["code", "policyNumber", "searchText"],
            clearable: true,
          },
        },
        {
          name: "user",
          parent: "main",
          component: RemoteSelect2,
          props: {
            label: "Consultant",
            apiEntity: "user",
            valueField: "_id",
            labelField: "name",
            labelRender: (data) =>
              `${data.name || ""} ${data.staffId || ""}`,
            preQuery: { isConsultant: true },
            searchFields: ["name", "cname", "aliasName", "staffId"],
            clearable: true,
          },
        },
        {
          name: "amount",
          parent: "main",
          component: NumberInput,
          props: {
            required: true,
            label: "Amount",
            description: "Positive adds points. Negative reverses a wrong posting.",
            precision: 2,
          },
        },
        {
          name: "remark",
          parent: "main",
          component: Textarea,
          props: {
            required: true,
            label: "Remark",
            placeholder: "Why this correction is needed",
            minRows: 2,
          },
        },
      ],
    },
  };

  return (
    <PageList
      title={"Point Transactions"}
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
    />
  );
};

export default PointTransaction;
